import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowDown10,
  ArrowDownAZ,
  ArrowUp10,
  ArrowUpAZ,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Mail,
  Paperclip,
  RefreshCw,
  Search,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAgent } from "@/hooks/agent";
import {
  useAgentMailboxConversation,
  useAgentMailboxConversations,
  useAgentMailboxRealtime,
  useSendAgentMailboxReply,
} from "@/hooks/agent/useAgentMailboxConversations";
import { useRole } from "@/hooks/auth";
import { useDebouncedValue } from "@/hooks/ui";
import { cn } from "@/lib/utils";

import type {
  MailboxConversationCustomer,
  MailboxConversationLastDecision,
  MailboxConversationSort,
  MailboxConversationState,
  MailboxConversationSummary,
  PipelineStepEntry,
} from "@/types/agent-mailboxes";

const DECISION_STATUS_OPTIONS = [
  { value: "auto_executed", label: "Auto-executed" },
  { value: "failed", label: "Failed" },
  { value: "proposed", label: "Proposed" },
];

const HG_MAIL_APP_URL = "https://email.helpgenie.ai";

const STATE_LABELS: Record<MailboxConversationState, string> = {
  open: "Open",
  escalated: "Escalated",
  closed: "Closed",
};

const getStateBadge = (state: MailboxConversationState) => {
  switch (state) {
    case "open":
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200">{STATE_LABELS[state]}</Badge>;
    case "escalated":
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">{STATE_LABELS[state]}</Badge>;
    case "closed":
      return <Badge variant="outline">{STATE_LABELS[state]}</Badge>;
    default:
      return <Badge variant="outline">{state}</Badge>;
  }
};

const formatDate = (iso: string) => new Date(iso).toLocaleString();

const ACTION_LABELS: Record<string, string> = {
  send: "Sent reply",
  forward: "Forwarded",
  escalate: "Escalated",
  wait: "Waiting",
  no_action: "No action",
};

const getLastDecisionBadge = (
  decision:
    | (Pick<MailboxConversationLastDecision, "action" | "status" | "confidence"> & { escalated?: boolean })
    | null,
  isOutbound?: boolean,
) => {
  if (!decision) {
    return isOutbound ? (
      <Badge className="bg-slate-100 text-slate-700 border-slate-200">Outbound</Badge>
    ) : (
      <Badge variant="outline">No decision yet</Badge>
    );
  }

  const label = ACTION_LABELS[decision.action] ?? decision.action;
  const confidencePct = decision.confidence !== null ? Math.round(decision.confidence * 100) : null;

  let badge: JSX.Element;
  if (decision.status === "failed") {
    badge = <Badge variant="destructive">{label} failed</Badge>;
  } else if (decision.escalated) {
    badge = <Badge className="bg-amber-100 text-amber-700 border-amber-200">{label}</Badge>;
  } else if (decision.status === "auto_executed") {
    badge = <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{label}</Badge>;
  } else {
    badge = <Badge variant="outline">{label}</Badge>;
  }

  return (
    <div className="flex items-center gap-1.5">
      {badge}
      {confidencePct !== null && <span className="text-xs text-genie-text-secondary">{confidencePct}%</span>}
    </div>
  );
};

const SenderCell = ({ customer }: { customer: MailboxConversationCustomer }) => {
  if (!customer.name && !customer.email) {
    return <span className="text-sm text-genie-text-secondary">—</span>;
  }

  return (
    <div className="text-sm">
      {customer.name && <div className="text-genie-text-primary truncate">{customer.name}</div>}
      {customer.email && (
        <CopyableEmail email={customer.email} className="text-xs text-genie-text-secondary" textClassName="truncate" />
      )}
    </div>
  );
};

const STEP_LABELS: Record<string, string> = {
  pipeline_started: "Pipeline started",
  human_takeover_guard: "Human takeover guard",
  loop_guard_check: "Loop guard check",
  triage_llm_call: "Triage",
  genie_grounding: "Genie grounding",
  compose_llm_call: "Compose",
  escalation_transform: "Escalation transform",
  decision_written: "Decision written",
  decision_executed: "Decision executed",
};

const getResultBadge = (result: PipelineStepEntry["result"]) => {
  switch (result) {
    case "success":
      return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Success</Badge>;
    case "error":
      return <Badge variant="destructive">Error</Badge>;
    case "skipped":
      return <Badge variant="outline">Skipped</Badge>;
    default:
      return <Badge variant="outline">{result}</Badge>;
  }
};

const PipelineSteps = ({ steps }: { steps: PipelineStepEntry[] }) => {
  const [openStepId, setOpenStepId] = useState<string | null>(null);

  if (steps.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-1.5">
      {steps.map((step) => {
        const hasDetail = Object.keys(step.detail).length > 0 || !!step.error;
        const isOpen = openStepId === step.id;
        return (
          <Collapsible key={step.id} open={isOpen} onOpenChange={(open) => setOpenStepId(open ? step.id : null)}>
            <div className="text-xs">
              <CollapsibleTrigger
                disabled={!hasDetail}
                className={cn(
                  "flex items-center justify-between w-full gap-2 text-left",
                  hasDetail ? "cursor-pointer" : "cursor-default",
                )}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  {hasDetail ? (
                    <ChevronRight className={cn("h-3 w-3 shrink-0 transition-transform", isOpen && "rotate-90")} />
                  ) : (
                    <span className="w-3 shrink-0" />
                  )}
                  <span className="font-medium text-genie-text-primary truncate">
                    {STEP_LABELS[step.step] ?? step.step}
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0 text-genie-text-secondary">
                  {step.latency_ms !== null && <span>{step.latency_ms}ms</span>}
                  {getResultBadge(step.result)}
                </span>
              </CollapsibleTrigger>
              {hasDetail && (
                <CollapsibleContent className="mt-1.5 ml-4.5">
                  {step.error && <p className="text-red-600 mb-1">{step.error}</p>}
                  {Object.keys(step.detail).length > 0 && (
                    <pre className="bg-genie-background-muted rounded-md p-2 text-[11px] overflow-x-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(step.detail, null, 2)}
                    </pre>
                  )}
                </CollapsibleContent>
              )}
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
};

const ReplyComposer = ({ agentId, conversationId }: { agentId: string; conversationId: string }) => {
  const [text, setText] = useState("");
  const { mutate: sendReply, isPending } = useSendAgentMailboxReply(agentId, conversationId);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    sendReply(trimmed, {
      onSuccess: () => {
        setText("");
        toast.success("Reply sent");
      },
      onError: () => {
        toast.error("Couldn't send the reply");
      },
    });
  };

  return (
    <div className="pt-3 border-t border-genie-border space-y-2">
      <h3 className="text-sm font-medium text-genie-text-primary">Reply</h3>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write a reply as the genie mailbox..."
        rows={4}
        disabled={isPending}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-genie-text-secondary">Sending this pauses the AI on this conversation.</span>
        <Button size="sm" onClick={handleSend} disabled={isPending || !text.trim()}>
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5 mr-1.5" />
          )}
          Send reply
        </Button>
      </div>
    </div>
  );
};

const GenieMailboxLogsPage = () => {
  const { genieId } = useParams<{ genieId: string }>();
  const { data: genie } = useAgent(genieId!);
  const { isInternalAdmin } = useRole();

  const [stateFilter, setStateFilter] = useState<MailboxConversationState | "all">("all");
  const [decisionStatusFilter, setDecisionStatusFilter] = useState<string | "all">("all");
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState<MailboxConversationSort>("date_desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(searchInput.trim(), 300);

  const filters = {
    ...(stateFilter !== "all" && { state: stateFilter }),
    ...(decisionStatusFilter !== "all" && { decisionStatus: decisionStatusFilter }),
    ...(debouncedSearch && { search: debouncedSearch }),
    sort,
  };
  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useAgentMailboxConversations(genieId, filters);

  useAgentMailboxRealtime(genieId);

  const { data: conversation, isLoading: isDetailLoading } = useAgentMailboxConversation(
    genieId,
    selectedId ?? undefined,
  );

  const conversations: MailboxConversationSummary[] = data?.pages.flatMap((page) => page.conversations) ?? [];

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1280px] mx-auto">
        <Link
          to={`/genie-hub/my-genies/${genieId}`}
          className="inline-flex items-center gap-1 text-sm text-genie-text-secondary hover:text-genie-text-primary mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to {genie?.name ?? "genie"}
        </Link>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Mail className="h-7 w-7 text-genie-secondary" />
            <div>
              <h1 className="text-2xl font-bold text-genie-text-primary">Mailbox Logs</h1>
              <p className="text-sm text-genie-text-secondary">
                Email conversations for{" "}
                <CopyableEmail email={genie?.mailbox?.email_address} fallback="this genie's mailbox" />
              </p>
            </div>
          </div>
          {genie?.mailbox?.account_id && (
            <a
              href={`${HG_MAIL_APP_URL}/home/mailboxes/${genie.mailbox.account_id}?tab=lifecycle`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-genie-text-secondary hover:text-genie-text-primary shrink-0"
            >
              View lifecycle data in HG Mail
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        <div className="bg-white rounded-lg p-4 mb-4 border border-genie-border flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-genie-text-secondary" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search sender or subject"
              className="w-64 pl-8"
            />
          </div>

          <Select
            value={stateFilter}
            onValueChange={(value) => setStateFilter(value as MailboxConversationState | "all")}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="escalated">Escalated</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={decisionStatusFilter} onValueChange={setDecisionStatusFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Last decision" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All decisions</SelectItem>
              {DECISION_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sort} onValueChange={(value) => setSort(value as MailboxConversationSort)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">
                <span className="flex items-center gap-1.5">
                  <ArrowDown10 className="h-3.5 w-3.5" /> Newest first
                </span>
              </SelectItem>
              <SelectItem value="date_asc">
                <span className="flex items-center gap-1.5">
                  <ArrowUp10 className="h-3.5 w-3.5" /> Oldest first
                </span>
              </SelectItem>
              <SelectItem value="sender_asc">
                <span className="flex items-center gap-1.5">
                  <ArrowDownAZ className="h-3.5 w-3.5" /> Sender A–Z
                </span>
              </SelectItem>
              <SelectItem value="sender_desc">
                <span className="flex items-center gap-1.5">
                  <ArrowUpAZ className="h-3.5 w-3.5" /> Sender Z–A
                </span>
              </SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching && !isFetchingNextPage}>
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", isFetching && !isFetchingNextPage && "animate-spin")} />
            Refresh
          </Button>
        </div>

        <div className="bg-white rounded-lg border border-genie-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sender</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Last decision</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>Last activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-genie-text-secondary">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                      Loading conversations...
                    </TableCell>
                  </TableRow>
                ) : conversations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-genie-text-muted">
                      No conversations found
                    </TableCell>
                  </TableRow>
                ) : (
                  conversations.map((c) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer hover:bg-genie-background-muted"
                      onClick={() => setSelectedId(c.id)}
                    >
                      <TableCell>
                        <SenderCell customer={c.customer} />
                      </TableCell>
                      <TableCell className="max-w-sm">
                        <div className="font-medium text-genie-text-primary truncate">
                          {c.subject ?? "(no subject)"}
                        </div>
                        {c.snippet && <div className="text-xs text-genie-text-secondary truncate">{c.snippet}</div>}
                      </TableCell>
                      <TableCell>{getLastDecisionBadge(c.last_decision, c.is_outbound)}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{c.message_count}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">
                        {formatDate(c.last_message_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {hasNextPage && (
            <div className="flex justify-center p-4 border-t border-genie-border">
              <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>

      <Sheet open={!!selectedId} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{conversation?.subject ?? "Conversation"}</SheetTitle>
          </SheetHeader>

          {isDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
            </div>
          ) : conversation ? (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-2 text-sm text-genie-text-secondary">
                <CopyableEmail
                  email={conversation.customer.name ?? conversation.customer.email}
                  fallback="Unknown customer"
                />
                {getStateBadge(conversation.state)}
                {getLastDecisionBadge(
                  conversation.decisions.length > 0 ? conversation.decisions[conversation.decisions.length - 1] : null,
                  conversation.is_outbound,
                )}
              </div>

              <div className="space-y-3">
                {conversation.messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      "rounded-lg border p-3 text-sm",
                      m.direction === "outbound"
                        ? "bg-genie-primary-light border-genie-primary/20 ml-6"
                        : "bg-genie-background-muted border-genie-border mr-6",
                    )}
                  >
                    <div className="flex items-center justify-between mb-1 gap-2 text-xs text-genie-text-secondary">
                      {m.sender ? (
                        <CopyableEmail email={m.sender} />
                      ) : (
                        <span>{m.direction === "outbound" ? "Genie" : "Customer"}</span>
                      )}
                      <span className="shrink-0">{formatDate(m.sent_at)}</span>
                    </div>
                    {m.direction === "outbound" && m.recipients && m.recipients.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mb-1.5 text-xs text-genie-text-secondary">
                        <span className="shrink-0">To:</span>
                        {m.recipients.map((r) => (
                          <CopyableEmail key={r} email={r} />
                        ))}
                      </div>
                    )}
                    {m.body_html ? (
                      <div
                        className="text-genie-text-primary [&_a]:text-genie-primary [&_a]:underline"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body_html) }}
                      />
                    ) : (
                      <p className="whitespace-pre-wrap text-genie-text-primary">{m.body_text ?? "(no content)"}</p>
                    )}
                    {m.attachments?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-genie-border/60 space-y-1">
                        {m.attachments.map((a) => (
                          <div
                            key={a.attachmentId}
                            className="flex items-center gap-1.5 text-xs text-genie-text-secondary"
                          >
                            <Paperclip className="h-3 w-3 shrink-0" />
                            <span className="truncate">{a.filename}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {conversation.decisions.length > 0 && (
                <div className="pt-2 border-t border-genie-border">
                  <h3 className="text-sm font-medium text-genie-text-primary mb-2">Decisions</h3>
                  <div className="space-y-2">
                    {conversation.decisions.map((d) => (
                      <div
                        key={d.id}
                        className="text-xs text-genie-text-secondary border border-genie-border rounded-md p-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-genie-text-primary">{d.action}</span>
                          <Badge variant="outline">{d.status}</Badge>
                        </div>
                        {d.confidence !== null && <div>Confidence: {Math.round(d.confidence * 100)}%</div>}
                        {d.reasoning && <p className="mt-1">{d.reasoning}</p>}
                        {d.pipeline.length > 0 && isInternalAdmin() && (
                          <Collapsible>
                            <CollapsibleTrigger className="flex items-center gap-1 mt-2 pt-2 border-t border-genie-border w-full text-genie-text-secondary hover:text-genie-text-primary [&[data-state=open]_svg]:rotate-90">
                              <ChevronRight className="h-3 w-3 transition-transform" />
                              <span className="font-medium">Pipeline ({d.pipeline.length} steps)</span>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <PipelineSteps steps={d.pipeline} />
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {genieId && <ReplyComposer agentId={genieId} conversationId={conversation.id} />}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default GenieMailboxLogsPage;
